import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Eye,
  Bell,
  Store,
  Wallet,
  MousePointer,
  Lock,
  Plus,
} from 'lucide-react';

import { Card } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { MetricCard } from '@/components/organisms/cards';
import { SubscriptionGate } from '@/lib/subscription';
import { useCanAccessAds } from '@/lib/subscription/hooks';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/molecules/tooltip';
import { useCampaigns, useAdWallet } from '@/features/advertising';
import { formatPrice, getAmountByCurrency } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { AdsHelpButton } from '@/features/advertising/components/ads-help-drawer';
import {
  ObjectiveCard,
  RecentCampaignsList,
  CompactStats,
  FloatingActionButton,
  ObjectiveBottomSheet,
} from '@/features/advertising/components/dashboard';

// ============ Objectives config ============

const objectives = [
  {
    title: 'Booster les ventes',
    description: 'Vos produits apparaissent en premier dans les recherches',
    icon: TrendingUp,
    href: '/ads/campaigns/new?type=sponsored_product',
    color: 'bg-green-500/10 text-green-600',
    price: 'À partir de 0.05€/clic',
  },
  {
    title: 'Être visible',
    description: 'Carousel homepage ou bannière en catégorie',
    icon: Eye,
    href: '/ads/campaigns/new?objective=visibility',
    color: 'bg-blue-500/10 text-blue-600',
    price: 'À partir de 8€/jour',
  },
  {
    title: 'Notifier mes clients',
    description: 'Notifications push ciblées vers vos abonnés',
    icon: Bell,
    href: '/ads/campaigns/new?objective=notification',
    color: 'bg-purple-500/10 text-purple-600',
    price: 'À partir de 3€/1000 envois',
  },
  {
    title: 'Mettre en avant ma boutique',
    description: 'Votre boutique en vitrine sur Volovolo',
    icon: Store,
    href: '/ads/campaigns/new?type=featured_shop',
    color: 'bg-orange-500/10 text-orange-600',
    price: '20€/semaine',
  },
];

// ============ Component ============

export default function AdsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currency } = useConfigStore();
  const canAccessAds = useCanAccessAds();

  // Bottom sheet state for mobile
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const { data: campaignsData, isLoading: campaignsLoading } = useCampaigns();
  const { data: wallet, isLoading: walletLoading } = useAdWallet();

  const campaigns = campaignsData?.campaigns ?? [];
  const totalSpent = campaigns.reduce(
    (sum, c) =>
      sum + getAmountByCurrency(currency, { eur: c.spentEur, usd: c.spentUsd, kmf: c.spentKmf }),
    0
  );
  const totalImpressions = campaigns.reduce((sum, c) => sum + (c.impressions ?? 0), 0);
  const totalClicks = campaigns.reduce((sum, c) => sum + (c.clicks ?? 0), 0);
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

  return (
    <div className="space-y-6 md:space-y-8 pb-24 md:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('ads.title')}</h1>
          <p className="text-muted-foreground hidden sm:block">
            {t('ads.subtitle', 'Gérez vos campagnes publicitaires et boostez vos ventes')}
          </p>
        </div>

        {/* Desktop buttons */}
        <div className="hidden sm:flex items-center gap-2">
          <AdsHelpButton />
          <Button variant="outline" asChild>
            <Link to="/ads/wallet">
              <Wallet className="size-4 mr-2" />
              {t('ads.rechargeWallet', 'Recharger')}
            </Link>
          </Button>
          {canAccessAds ? (
            <Button asChild>
              <Link to="/ads/campaigns/new">
                <Plus className="size-4 mr-2" />
                {t('ads.newCampaign')}
              </Link>
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button disabled className="cursor-not-allowed">
                  <Lock className="size-4 mr-2" />
                  {t('ads.newCampaign')}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('subscription.upgradeRequired', 'Abonnement Medium ou supérieur requis')}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* Mobile buttons - only wallet */}
        <div className="flex sm:hidden items-center gap-2 justify-end">
          <AdsHelpButton />
          <Button variant="outline" size="icon" asChild>
            <Link to="/ads/wallet">
              <Wallet className="size-4" />
            </Link>
          </Button>
        </div>
      </div>

      <SubscriptionGate feature="ads">
        {/* Desktop: MetricCards grid */}
        <div className="hidden md:block">
          {campaignsLoading || walletLoading ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="py-4">
                  <div className="px-4 space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-8 w-32" />
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div
                onClick={() => navigate('/ads/wallet')}
                className="cursor-pointer"
              >
                <MetricCard
                  title={t('ads.adBalance')}
                  value={formatPrice(
                    getAmountByCurrency(currency, {
                      eur: wallet?.balanceEur,
                      usd: wallet?.balanceUsd,
                      kmf: wallet?.balanceKmf,
                    }),
                    currency
                  )}
                  icon={Wallet}
                  className="hover:border-primary/50 transition-colors"
                />
              </div>
              <MetricCard
                title={t('ads.totalSpent')}
                value={formatPrice(totalSpent, currency)}
                icon={TrendingUp}
              />
              <MetricCard
                title={t('ads.impressions')}
                value={totalImpressions.toLocaleString()}
                icon={Eye}
              />
              <MetricCard
                title={t('ads.ctr')}
                value={`${avgCtr.toFixed(2)}%`}
                icon={MousePointer}
              />
            </div>
          )}
        </div>

        {/* Mobile: Compact stats (horizontal scroll) */}
        <div className="md:hidden">
          <CompactStats onWalletClick={() => navigate('/ads/wallet')} />
        </div>

        {/* Desktop: Objectives Section */}
        <div className="hidden md:block space-y-4">
          <h2 className="text-lg font-semibold">
            {t('ads.objectives.title', 'Quel est votre objectif ?')}
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {objectives.map((objective) => (
              <ObjectiveCard
                key={objective.href}
                title={objective.title}
                description={objective.description}
                icon={objective.icon}
                href={objective.href}
                color={objective.color}
                price={objective.price}
              />
            ))}
          </div>
        </div>

        {/* Recent Campaigns - show on both mobile and desktop */}
        <RecentCampaignsList limit={5} />

        {/* Mobile: FAB + Bottom Sheet */}
        {canAccessAds && (
          <div className="md:hidden">
            <FloatingActionButton onClick={() => setIsSheetOpen(true)} />
            <ObjectiveBottomSheet
              open={isSheetOpen}
              onOpenChange={setIsSheetOpen}
            />
          </div>
        )}
      </SubscriptionGate>
    </div>
  );
}
